import { FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { arrowPulsing } from '@/modules/homepage/Advantages';

import Slide from './Slide';

const SliderElement: FC<any> = ({ slides }) => {
	const slideRow = useRef<any>(null);

	const [activeSlide, changeActiveSlide] = useState(1);
	const [slidesPerScreen, changeSlidesPerScreen] = useState(2);

	const [touchStart, setTouchStart] = useState(0);
	const [touchEnd, setTouchEnd] = useState(0);

	useEffect(() => {
		if (window.innerWidth < 600) changeSlidesPerScreen(1);
	}, []);

	const changeSlide = (slide) => {
		changeActiveSlide(slide);
	};

	const left = () => {
		if (activeSlide === 0) {
			slideRow.current?.classList?.add('toend');

			setTimeout(() => {
				changeSlide(slides?.length / slidesPerScreen - 1);
			}, 100);
		} else {
			changeSlide(activeSlide - 1);
		}
	};

	const right = () => {
		if (activeSlide === slides?.length / slidesPerScreen) {
			slideRow.current.classList.add('tostart');

			setTimeout(() => {
				changeSlide(1);
			}, 100);
		} else {
			changeSlide(activeSlide + 1);
		}
	};

	const handleTouchStart = (e) => {
		setTouchStart(e.targetTouches[0].clientX);
	};

	const handleTouchMove = (e) => {
		setTouchEnd(e.targetTouches[0].clientX);
	};

	const handleTouchEnd = () => {
		if (touchStart - touchEnd > 80) {
			right();
		}

		if (touchStart - touchEnd < -80) {
			left();
		}
	};

	return (
		<Slider>
			<RowWrapper>
				<Row
					ref={slideRow}
					$moveLoopStart={0}
					$move={activeSlide * 100 * -1}
					$moveLoopEnd={(slides?.length / slidesPerScreen) * 100 * -1}
				>
					{slides
						.slice(slides?.length - slidesPerScreen, slides?.length)
						.map((item, index) => (
							<Slide
								key={`stories_slide_1_${index}`}
								slide={item}
								handleTouchStart={handleTouchStart}
								handleTouchEnd={handleTouchEnd}
								handleTouchMove={handleTouchMove}
							/>
						))}

					{slides?.map((item, index) => (
						<Slide
							key={`stories_slide_2_${index}`}
							slide={item}
							handleTouchStart={handleTouchStart}
							handleTouchEnd={handleTouchEnd}
							handleTouchMove={handleTouchMove}
						/>
					))}

					{slides
						?.slice(0, 2)
						.map((item, index) => (
							<Slide
								key={`stories_slide_3_${index}`}
								slide={item}
								handleTouchStart={handleTouchStart}
								handleTouchEnd={handleTouchEnd}
								handleTouchMove={handleTouchMove}
							/>
						))}
				</Row>
			</RowWrapper>

			<Dots>
				{slides?.slice(0, slides?.length / slidesPerScreen).map((_, index) => {
					const isActive =
						index === activeSlide - 1 ||
						(index === slides?.length / slidesPerScreen - 1 &&
							activeSlide === 0);

					return (
						<Dot
							key={`stories_slider_dot_${index}`}
							$active={isActive}
							onClick={() => !isActive && changeSlide(index + 1)}
						/>
					);
				})}
			</Dots>

			<Arrows>
				<Left onClick={left}>
					<img alt="Left Arrow" src="/images/icons/slider/left-arrow.svg" />
				</Left>

				<Right onClick={right}>
					<img alt="Right Arrow" src="/images/icons/slider/right-arrow.svg" />
				</Right>
			</Arrows>
		</Slider>
	);
};

const Slider = styled.div`
	position: relative;
	padding: 0 15px;
`;

const RowWrapper = styled.div`
	position: relative;
	overflow: hidden;
	max-width: 100%;
`;

const Row = styled.div<{
	$move: number;
	$moveLoopStart: number;
	$moveLoopEnd: number;
}>`
	display: flex;
	transform: translateX(${({ $move }) => $move}%);
	transition: 0.4s;
	margin-left: -30px;
	margin-right: -30px;
	will-change: transform;

	&.tostart {
		transition: 0s;
		transform: translateX(${({ $moveLoopStart }) => $moveLoopStart}%);
	}

	&.toend {
		transition: 0s;
		transform: translateX(${({ $moveLoopEnd }) => $moveLoopEnd}%);
	}

	.slide-item {
		padding-right: 30px;
		padding-left: 30px;
	}
`;

const Arrows = styled.div`
	position: absolute;
	top: 190px;
	left: 0;
	width: 100%;

	@media (max-width: 490px) {
		top: 222px;
	}

	@media (max-width: 375px) {
		top: 190px;
	}
`;

const Arrow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	width: 50px;
	height: 50px;
	top: 0;
	background: linear-gradient(
		180deg,
		var(--color-purple) 0%,
		var(--color-purple-80) 100%
	);
	border-radius: 50%;
	cursor: pointer;
	visibility: hidden;

	@media (max-width: 600px) {
		visibility: visible;
	}

	@media (max-width: 350px) {
		width: 45px;
		height: 45px;
		top: 30px;
	}

	@media (max-width: 320px) {
		width: 40px;
		height: 40px;
	}

	img {
		animation-name: ${arrowPulsing};
		animation-duration: 1.7s;
		animation-iteration-count: infinite;
		animation-fill-mode: both;
		animation-timing-function: linear;
		animation-play-state: running;
	}
`;

const Left = styled(Arrow)`
	left: 2%;
`;

const Right = styled(Arrow)`
	right: 2%;
`;

const Dots = styled.div`
	width: 100%;
	display: flex;
	margin-top: 37px;
	align-items: center;
	justify-content: center;
`;

const Dot = styled.div<{ $active: boolean }>`
	width: 20px;
	height: 20px;
	position: relative;
	margin-right: 4px;
	margin-left: 4px;
	cursor: pointer;

	&:after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 4px;
		height: 4px;
		background-color: var(--color-purple);
		border-radius: 50%;
		transition: 0.3s;

		${({ $active }) =>
			$active &&
			css`
				width: 13px;
				height: 13px;
			`}
	}
`;

export default SliderElement;
